import Header from '../components/Header';
import Footer from '../components/Footer';

const sections = [
	{
		id: 'effective-date',
		title: 'Effective Date',
		content: (
			<p className="text-slate-600 leading-relaxed">Effective Date: June 5, 2024</p>
		),
	},
	{
		id: 'information-we-collect',
		title: '1. Information We Collect',
		content: (
			<div className="space-y-4">
				<div>
					<h3 className="text-base font-semibold text-slate-800 mb-2">1.1 Information You Provide</h3>
					<ul className="list-disc pl-6 space-y-1 text-slate-600">
						<li>Forms, registrations, and account creation</li>
						<li>Newsletter subscriptions &amp; communications</li>
						<li>Support requests &amp; surveys</li>
						<li>Contact enquiries submitted through our website</li>
					</ul>
				</div>
				<div>
					<h3 className="text-base font-semibold text-slate-800 mb-2">1.2 Third-Party Platforms</h3>
					<p className="text-slate-600 leading-relaxed">
						Where third-party login or integration services are used, we may receive basic profile data, access tokens, and app usage data as permitted by those platforms.
					</p>
				</div>
				<div>
					<h3 className="text-base font-semibold text-slate-800 mb-2">1.3 Automatically Collected</h3>
					<ul className="list-disc pl-6 space-y-1 text-slate-600">
						<li>Usage &amp; interaction analytics</li>
						<li>Device &amp; log info (IP address, browser type, operating system)</li>
						<li>Cookies &amp; similar technologies</li>
						<li>Platform performance metrics</li>
					</ul>
				</div>
			</div>
		),
	},
	{
		id: 'how-we-use',
		title: '2. How We Use Your Information',
		content: (
			<div className="space-y-4">
				<ul className="list-disc pl-6 space-y-1 text-slate-600">
					<li>Provide, improve &amp; personalise our services</li>
					<li>Process requests &amp; provide support</li>
					<li>Send communications about products &amp; updates</li>
					<li>Security, fraud prevention &amp; compliance</li>
					<li>Analytics, research &amp; product development</li>
					<li>Third-party platform integrations (where applicable)</li>
				</ul>
			</div>
		),
	},
	{
		id: 'disclosure',
		title: '3. Disclosure of Information',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600">
				<li>Service providers &amp; vetted processors</li>
				<li>Third-party integrations where required for service delivery</li>
				<li>Business transfers (merger / acquisition)</li>
				<li>Legal obligations &amp; protection of rights</li>
				<li>With your explicit consent</li>
			</ul>
		),
	},
	{
		id: 'data-security',
		title: '4. Data Security',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Technical &amp; organisational controls are applied including encryption in transit, access scoping, and regular audits. No system is 100% secure; breach notifications will be issued as required by law.
			</p>
		),
	},
	{
		id: 'your-rights',
		title: '5. Your Rights &amp; Choices',
		content: (
			<ul className="list-disc pl-6 space-y-1 text-slate-600">
				<li>Access, Rectification &amp; Deletion of your data</li>
				<li>Restriction of or objection to processing</li>
				<li>Data portability &amp; withdrawal of consent</li>
				<li>App permission management via third-party platforms</li>
			</ul>
		),
	},
	{
		id: 'cookies',
		title: '6. Cookies &amp; Similar Technologies',
		content: (
			<p className="text-slate-600 leading-relaxed">
				We use cookies for session continuity, analytics, and preference storage. Browser controls allow opting out, though some features may degrade. See our <a className="text-cyan-600 hover:underline" href="/cookie-policy">Cookie Policy</a> for full details.
			</p>
		),
	},
	{
		id: 'childrens-privacy',
		title: '7. Children\'s Privacy',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Our services are not directed to children under 13. Any inadvertent collection of such data will trigger prompt deletion upon discovery.
			</p>
		),
	},
	{
		id: 'international-transfers',
		title: '8. International Data Transfers',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Data may be processed in other jurisdictions with appropriate safeguards including contractual protections, data minimisation, and regional compliance measures.
			</p>
		),
	},
	{
		id: 'data-retention',
		title: '9. Data Retention',
		content: (
			<div className="space-y-3">
				<p className="text-slate-600 leading-relaxed">
					Data is retained only as long as necessary for the stated purpose or applicable legal requirements.
				</p>
				<ul className="list-disc pl-6 space-y-1 text-slate-600">
					<li>Authentication data: while account is active</li>
					<li>Usage analytics: ≤ 24 months (anonymised thereafter)</li>
					<li>Deletion requests: purge within 30 days (metadata may persist in anonymised form)</li>
				</ul>
			</div>
		),
	},
	{
		id: 'changes',
		title: '10. Changes to this Policy',
		content: (
			<p className="text-slate-600 leading-relaxed">
				Material updates will be announced via site notice or email. Continued use of our services after the effective date constitutes acceptance of the updated policy.
			</p>
		),
	},
	{
		id: 'contact',
		title: '11. Contact Us',
		content: (
			<div className="space-y-2 text-slate-600">
				<p className="font-semibold text-slate-800">VIANCE HEALTHCARE SYSTEMS PRIVATE LIMITED</p>
				<p>Sulochana Apartments, Income Tax Lane, Pune – 411004, Maharashtra, India</p>
				<p>
					Email:{' '}
					<a className="text-cyan-600 hover:underline" href="mailto:admin@viance.co.in">
						admin@viance.co.in
					</a>
				</p>
				<p>
					Phone:{' '}
					<a className="text-cyan-600 hover:underline" href="tel:+919823272353">
						+91 9823272353
					</a>
				</p>
			</div>
		),
	},
];

function PrivacyPolicy() {
	return (
		<div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100 to-slate-100 text-slate-900">
			<Header />
			<main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
				{/* Page header */}
				<div className="mb-10">
					<span className="inline-block rounded-full bg-cyan-100 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-cyan-700">
						Legal
					</span>
					<h1 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900">
						Privacy Policy
					</h1>
					<p className="mt-3 max-w-2xl text-slate-600">
						How Viance collects, uses, shares and protects information across our web and platform integrations.
					</p>
					<p className="mt-2 text-sm text-slate-400">Last updated: 2026</p>
				</div>

				{/* Table of contents */}
				<nav className="mb-10 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
					<h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-cyan-700">Contents</h2>
					<ol className="space-y-1 text-sm text-slate-600">
						{sections.map((s) => (
							<li key={s.id}>
								<a href={`#${s.id}`} className="hover:text-cyan-600 transition-colors">
									{s.title}
								</a>
							</li>
						))}
					</ol>
				</nav>

				{/* Sections */}
				<div className="space-y-10 rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
					{sections.map((s, i) => (
						<section key={s.id} id={s.id} className={i !== 0 ? 'border-t border-slate-100 pt-8' : ''}>
							<h2 className="mb-4 text-xl font-bold text-slate-900"
								dangerouslySetInnerHTML={{ __html: s.title }}
							/>
							{s.content}
						</section>
					))}
				</div>
			</main>
			<Footer />
		</div>
	);
}

export default PrivacyPolicy;
